import { useState, useEffect, useRef, useContext } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaUser, FaLeaf, FaWhatsapp, FaSync } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import HttpInterceptor from '../lib/HttpInterceptor';
import TrainerAvailabilityNotificationModal from './TrainerAvailabilityNotificationModal';
import { formatCurrency } from '../utils/currencyFormatter';
import UserContext from './UserContext';

// Using a reliable avatar service - if this fails, fallback to "P" initial will show
// Alternative: You can replace this with any image URL you prefer
const priyaAvatar = '/assets/ai-agents/priya_representative.png';

/**
 * Friendly ChatBot Component
 * Provides a warm, representative-like experience with predefined FAQs
 */

// FAQ data with friendly, conversational responses
const faqData = [
  {
    keywords: ['sign up', 'signup', 'register', 'create account', 'join', 'become member', 'how to join'],
    question: 'How do I sign up?',
    answer: `Great question! Signing up is super easy. Just click the "Get Started" or "Sign Up" button on our landing page, and you'll be guided through a simple registration process. 

You can sign up as either a member (student) or as a trainer. The process takes just a few minutes, and you'll have access to all our amazing yoga programs and services right away! 

Would you like me to help you with anything specific about the sign-up process?`,
    quickActions: [
      { text: 'Go to Sign Up', link: '/signup' },
      { text: 'View Programs', link: '/market' }
    ]
  },
  {
    keywords: ['program', 'programs', 'course', 'courses', 'training', 'what do you offer', 'services'],
    question: 'What programs do you offer?',
    answer: `We're so excited to share our offerings with you! At Yogvital, we offer a wonderful variety of wellness programs:

🧘 **Yoga Training** - From beginner to advanced levels
🥗 **Dietitian Services** - Personalized nutrition guidance
💪 **Gym Instruction** - Fitness and strength training
💃 **Zumba Classes** - Fun, energetic dance fitness
✨ **Reiki Healing** - Energy healing sessions
🔮 **Astrology & Numerology** - Spiritual guidance
🌿 **Ayurveda** - Traditional wellness practices

To see the specific programs available, please select a service type below and I'll show you what's available!`,
    quickActions: [
      { text: 'Yoga Trainer', action: 'select-service:Yoga Trainer' },
      { text: 'Dietitian', action: 'select-service:Dietitian' },
      { text: 'Gym Instructor', action: 'select-service:Gym Instructor' },
      { text: 'Zumba Instructor', action: 'select-service:Zumba Instructor' },
      { text: 'Reiki Healer', action: 'select-service:Reiki Healer' },
      { text: 'Astrologer', action: 'select-service:Astrologer' },
      { text: 'Numerologer', action: 'select-service:Numerologer' },
      { text: 'Ayurveda Practitioner', action: 'select-service:Ayurveda Practitioner' }
    ]
  },
  {
    keywords: ['class', 'classes', 'schedule', 'scheduling', 'session', 'sessions', 'when are classes', 'class timing', 'class schedule'],
    question: 'How do classes work?',
    answer: `Great question! Our classes are designed to fit your lifestyle and wellness goals. Here's how they work:

📅 **Flexible Scheduling** - Each trainer sets their own class schedules, so you can find times that work for you
👥 **Individual or Group** - Choose between one-on-one sessions or group classes based on your preference
⏰ **Session Duration** - Classes typically range from 30 minutes to 90 minutes, depending on the program
📱 **Easy Booking** - Once enrolled, you can view and book classes directly from your dashboard
🔄 **Regular Sessions** - Most programs include weekly or bi-weekly sessions to help you stay consistent

To see specific class schedules and availability, please select a service type below and I'll show you the programs with their class details!`,
    quickActions: [
      { text: 'Yoga Trainer', action: 'select-service:Yoga Trainer' },
      { text: 'Dietitian', action: 'select-service:Dietitian' },
      { text: 'Gym Instructor', action: 'select-service:Gym Instructor' },
      { text: 'Zumba Instructor', action: 'select-service:Zumba Instructor' },
      { text: 'Reiki Healer', action: 'select-service:Reiki Healer' },
      { text: 'Astrologer', action: 'select-service:Astrologer' },
      { text: 'Numerologer', action: 'select-service:Numerologer' },
      { text: 'Ayurveda Practitioner', action: 'select-service:Ayurveda Practitioner' }
    ]
  },
  {
    keywords: ['price', 'pricing', 'cost', 'fee', 'fees', 'how much', 'payment', 'pay', 'expensive', 'affordable'],
    question: 'How much does it cost?',
    answer: `I'd be happy to help you understand our pricing! Our programs are designed to be accessible and offer great value. 
    
Pricing varies depending on:
• The type of program you choose
• The duration and frequency of sessions
• Whether you opt for individual or group sessions

Each trainer sets their own rates, so you can find options that fit your budget. Many of our trainers offer flexible payment plans too!

To see exact pricing, please select a service type below and I'll show you the available programs with their pricing!`,
    quickActions: [
      { text: 'Yoga Trainer', action: 'select-service:Yoga Trainer' },
      { text: 'Dietitian', action: 'select-service:Dietitian' },
      { text: 'Gym Instructor', action: 'select-service:Gym Instructor' },
      { text: 'Zumba Instructor', action: 'select-service:Zumba Instructor' },
      { text: 'Reiki Healer', action: 'select-service:Reiki Healer' },
      { text: 'Astrologer', action: 'select-service:Astrologer' },
      { text: 'Numerologer', action: 'select-service:Numerologer' },
      { text: 'Ayurveda Practitioner', action: 'select-service:Ayurveda Practitioner' }
    ]
  },
  {
    keywords: ['trainer', 'instructor', 'teacher', 'contact trainer', 'message trainer', 'talk to trainer'],
    question: 'How do I contact my trainer?',
    answer: `Absolutely! Connecting with your trainer is easy. Once you're enrolled in a program, you can message your trainer directly from your student dashboard.

Here's how:
1. Log in to your account
2. Go to "My Trainer" section
3. Click on your trainer's profile
4. Use the "Message" button to start a conversation

Your trainer will receive your message and get back to you as soon as possible. It's a great way to ask questions, discuss your progress, or schedule sessions!

Need help accessing your dashboard?`,
    quickActions: [
      { text: 'Go to Login', link: '/login' },
      { text: 'View Trainers', link: '/market' }
    ]
  },
  {
    keywords: ['login', 'sign in', 'log in', 'account', 'forgot password', 'reset password'],
    question: 'How do I log in?',
    answer: `No worries! Logging in is simple. Just click the "Sign In" button at the top of the page, or visit our login page directly.

You can sign in using:
• Your email address and password
• Your mobile number and password
• Google account (if you signed up with Google)

Forgot your password? No problem! Click "Forgot password?" on the login page, and we'll help you reset it right away.

Is there anything specific about logging in that I can help with?`,
    quickActions: [
      { text: 'Go to Login', link: '/login' },
      { text: 'Reset Password', link: '/forgot-password' }
    ]
  },
  {
    keywords: ['install', 'download', 'app', 'mobile app', 'pwa', 'add to home', 'install app'],
    question: 'Can I install Yogvital as an app?',
    answer: `Yes, absolutely! Yogvital works great as a Progressive Web App (PWA), which means you can install it on your phone just like a regular app!

Here's how:
• **On Android**: Look for the "Install App" prompt, or use your browser menu (⋮) and select "Install app"
• **On iOS**: Tap the Share button and select "Add to Home Screen"

Once installed, you'll have quick access to all your classes, programs, and trainers right from your home screen. It's super convenient!

Would you like step-by-step instructions?`,
    quickActions: [
      { text: 'Install Instructions', link: '/landing#pwa-install' },
      { text: 'Go to Landing', link: '/' }
    ]
  },
  {
    keywords: ['help', 'support', 'contact', 'reach out', 'talk to someone', 'customer service', 'assistance', 'human', 'person', 'agent'],
    question: 'How can I get help?',
    answer: `I'm here to help! You can reach us in several ways:

💬 **Right here** - I can answer your questions instantly
📱 **WhatsApp** - Park your message and get a reply soon within official hours
📧 **Contact Form** - Fill out our contact form for detailed inquiries
💬 **In-App Messaging** - Message your trainer directly from your dashboard

For account-related issues, password resets, or billing questions, our support team is ready to assist you. Would you like to chat with a real person on WhatsApp?`,
    quickActions: [
      { text: 'Chat on WhatsApp', action: 'whatsapp' },
      { text: 'Contact Form', link: '/contact-form' },
      { text: 'View FAQs', action: 'show-faqs' }
    ]
  },
  {
    keywords: ['cancel', 'refund', 'money back', 'unsubscribe', 'leave', 'stop'],
    question: 'Can I cancel my subscription?',
    answer: `I understand you might need to make changes to your subscription. Our cancellation and refund policies depend on your specific program and payment plan.

For the most accurate information about:
• Cancellation procedures
• Refund eligibility
• Payment plan modifications

I'd recommend reaching out to your trainer directly through the messaging feature, or contacting our support team. They'll be able to review your specific situation and help you with the best options available.

Is there a specific reason you're considering canceling? Sometimes we can help find a solution that works better for you!`,
    quickActions: [
      { text: 'Contact Support', action: 'contact' },
      { text: 'Message Trainer', link: '/student/trainer' }
    ]
  }
];

// Greeting message - personalized for Priya
const greeting = "Hello! I'm Priya, your Yogvital Assistant. How can I help you today? 👋";

const ChatBot = () => {
  const { session } = useContext(UserContext);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    // Load messages from sessionStorage if available
    try {
      const savedMessages = sessionStorage.getItem('chatbot-messages');
      return savedMessages ? JSON.parse(savedMessages) : [];
    } catch {
      return [];
    }
  });
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationService, setNotificationService] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const lastInteractionRef = useRef(null);

  // Start inactivity timer - shows follow-up message after 60 seconds
  const startInactivityTimer = () => {
    // Clear any existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Set new timer for 60 seconds (1 minute)
    inactivityTimerRef.current = setTimeout(() => {
      // Check if user hasn't interacted in the last minute
      const timeSinceLastInteraction = Date.now() - (lastInteractionRef.current || Date.now());
      if (timeSinceLastInteraction >= 60000 && messages.length > 0) {
        // Only show if there are messages and no recent interaction
        const lastMessage = messages[messages.length - 1];
        // Don't show if the last message is already the follow-up question
        if (lastMessage && lastMessage.text !== "Is there anything else I can help you with? 😊") {
          addMessage('bot', "Is there anything else I can help you with? 😊", [
            'Contact Support',
            'No, thanks!'
          ]);
        }
      }
    }, 60000); // 60 seconds
  };

  // Reset inactivity timer on any user interaction
  const resetInactivityTimer = () => {
    lastInteractionRef.current = Date.now();
    startInactivityTimer();
  };

  // Initialize with greeting only if no messages exist
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage('bot', greeting);
      lastInteractionRef.current = Date.now();
      // Start inactivity timer - show follow-up after 60 seconds of no interaction
      startInactivityTimer();
    } else if (isOpen && messages.length > 0) {
      // Restart inactivity timer if messages exist
      resetInactivityTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, messages.length]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const addMessage = (sender, text, quickActions = null) => {
    setMessages(prev => {
      const newMessages = [...prev, { sender, text, quickActions, timestamp: new Date() }];
      // Save to sessionStorage
      try {
        sessionStorage.setItem('chatbot-messages', JSON.stringify(newMessages));
      } catch {
        // Ignore storage errors
      }
      return newMessages;
    });
  };

  const showQuickQuestions = () => {
    const quickQuestions = [
      'How do I sign up?',
      'What programs do you offer?',
      'How much does it cost?',
      'How do I contact my trainer?',
      'How do I log in?',
      'Can I install Yogvital as an app?',
      'Can I cancel my subscription?',
      'Contact Support'
    ];
    addMessage('bot', "Here are all the questions I can help you with:", quickQuestions);
  };

  const findAnswer = (userInput) => {
    const lowerInput = userInput.toLowerCase().trim();
    
    // Check for exact or close keyword matches
    for (const faq of faqData) {
      for (const keyword of faq.keywords) {
        if (lowerInput.includes(keyword)) {
          return faq;
        }
      }
    }
    
    // Check for greeting/hello patterns
    if (lowerInput.match(/^(hi|hello|hey|good morning|good afternoon|good evening|greetings)/)) {
      return {
        answer: "Hello! 😊 It's so nice to meet you! I'm here to help you with anything you need about Yogvital. What would you like to know?",
        quickActions: null
      };
    }
    
    // Check for thanks/gratitude
    if (lowerInput.match(/(thank|thanks|appreciate|grateful)/)) {
      return {
        answer: "You're so welcome! 😊 Feel free to reach out anytime if you need anything else. Have a wonderful day!",
        quickActions: null
      };
    }
    
    return null;
  };

  const handleSendMessage = async (message = null) => {
    const userMessage = message || inputValue.trim();
    if (!userMessage) return;

    // Reset inactivity timer on user message
    resetInactivityTimer();

    // Add user message
    addMessage('user', userMessage);
    setInputValue('');

    // Show typing indicator - simulate reading and understanding the question
    setIsTyping(true);

    // Longer thinking time to simulate human reading and understanding
    // More complex questions = longer thinking time
    const messageLength = userMessage.length;
    const baseDelay = 1200; // Base delay of 1.2 seconds
    const lengthDelay = Math.min(messageLength * 10, 2000); // Up to 2 seconds based on message length
    const randomDelay = Math.random() * 800; // Random 0-800ms for natural variation
    const totalDelay = baseDelay + lengthDelay + randomDelay;

    setTimeout(() => {
      setIsTyping(false);
      
      // Small pause before showing answer (simulates formulating response)
      setTimeout(async () => {
        const faq = findAnswer(userMessage);
        
      if (faq) {
        addMessage('bot', faq.answer, faq.quickActions);
        // Reset inactivity timer - don't show follow-up immediately
        resetInactivityTimer();
        setIsTyping(false);
      } else {
          // AI Response for "vigorous queries"
          try {
            const response = await HttpInterceptor.post('/api/ai/general-chat', {
              message: userMessage,
              history: messages.slice(-6), // Send last 6 messages for context
              type: 'public'
            });

            if (response.data?.success) {
              addMessage('bot', response.data.data.text);
            } else {
              throw new Error('AI response failed');
            }
          } catch (error) {
            console.error("AI Chat Error:", error);
            // Fallback response when AI query fails
            addMessage('bot', `That's a great question! While I might not have all the details on that, our support team would be happy to help you with a more detailed answer. You can park your message on WhatsApp, and they'll get back to you soon within official hours! 😊`, [
              { text: 'Connect with Support', action: 'whatsapp' },
              { text: 'Ask Something Else', action: 'show-faqs' }
            ]);
          } finally {
            setIsTyping(false);
          }
        }
      }, 300); // Small pause after typing stops
    }, totalDelay);
  };

  const fetchProgramsForService = async (serviceName) => {
    try {
      setIsLoadingPrograms(true);
      setIsTyping(true);
      
      let response;
      let servicesData = [];
      
      try {
        response = await HttpInterceptor.get('/api/students/programs');
        
        // Handle successful response (200 or 2xx)
        if (response.status >= 200 && response.status < 300) {
          servicesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        } else {
          // Non-2xx status - treat as no data available (not an error)
          servicesData = [];
        }
      } catch (apiError) {
        // If API returns 404 or similar, it might mean no data - treat as "no programs available"
        // Only treat as actual error if it's a server error (5xx) or network error
        const status = apiError.response?.status;
        const isServerError = status >= 500;
        const isNetworkError = apiError.code === 'ERR_NETWORK' || apiError.message?.includes('Network');
        
        if (isServerError || isNetworkError) {
          // Re-throw actual errors
          throw apiError;
        }
        
        // For 404 or other 4xx (except 400), assume no data available
        servicesData = [];
      }
      
      setIsTyping(false);
      setIsLoadingPrograms(false);
      
      // Find the service
      let service = servicesData.find(s => s.name === serviceName || s.type === serviceName);
      
      // If not found by name, try to find by matching trainers' service field
      if (!service && servicesData.length > 0) {
        // Look for trainers with matching service
        for (const s of servicesData) {
          if (s.trainers && s.trainers.some(t => t.service === serviceName)) {
            service = {
              ...s,
              trainers: s.trainers.filter(t => t.service === serviceName)
            };
            break;
          }
        }
      }
      
        // No service found - this is not an error, just no programs available
        if (!service || !service.trainers || service.trainers.length === 0) {
          addMessage('bot', `I checked, but there are currently no programs available for ${serviceName}. 😔\n\nWould you like me to notify you when programs become available for this service?`, [
            { text: 'Yes, Notify Me', action: `notify-service:${serviceName}` },
            { text: 'Check Another Service', action: 'show-services' }
          ]);
          return;
        }
      
      // Collect all programs from all trainers
      const allPrograms = [];
      service.trainers.forEach(trainer => {
        if (trainer.programs && trainer.programs.length > 0) {
          trainer.programs.forEach(program => {
            allPrograms.push({
              ...program,
              trainerName: trainer.name || 'Trainer',
              trainerId: trainer.id || trainer._id
            });
          });
        }
      });
      
        // No programs found - this is not an error, just no programs available
        if (allPrograms.length === 0) {
          addMessage('bot', `I found trainers for ${serviceName}, but there are currently no programs available. 😔\n\nWould you like me to notify you when programs become available for this service?`, [
            { text: 'Yes, Notify Me', action: `notify-service:${serviceName}` },
            { text: 'Check Another Service', action: 'show-services' }
          ]);
          return;
        }
      
      // Format programs for display
      let programsText = `Great! I found ${allPrograms.length} program${allPrograms.length > 1 ? 's' : ''} available for ${serviceName}:\n\n`;
      
      allPrograms.slice(0, 5).forEach((program, index) => {
        programsText += `${index + 1}. **${program.name || 'Program'}**\n`;
        if (program.description) {
          programsText += `   ${program.description.substring(0, 100)}${program.description.length > 100 ? '...' : ''}\n`;
        }
        if (program.trainerName) {
          programsText += `   👤 Trainer: ${program.trainerName}\n`;
        }
        // Add pricing information if available
        if (program.price || program.basePrice) {
          const price = program.price || program.basePrice || 0;
          if (program.discountInfo && program.discountInfo.discountedPrice) {
            programsText += `   💰 Price: ${formatCurrency(program.discountInfo.originalPrice)} → ${formatCurrency(program.discountInfo.discountedPrice)} (Discounted)\n`;
          } else {
            programsText += `   💰 Price: ${formatCurrency(price)}\n`;
          }
        }
        programsText += `\n`;
      });
      
      if (allPrograms.length > 5) {
        programsText += `\n...and ${allPrograms.length - 5} more program${allPrograms.length - 5 > 1 ? 's' : ''}!\n\n`;
      }
      
      programsText += `Would you like to view all programs with detailed pricing and enroll?`;
      
      addMessage('bot', programsText, [
        { text: 'View All Programs', link: `/market?service=${encodeURIComponent(serviceName)}` },
        { text: 'Check Another Service', action: 'show-services' }
      ]);
      
    } catch (error) {
      setIsTyping(false);
      setIsLoadingPrograms(false);
      
      // Only show error message for actual network/API errors (5xx or network errors)
      // 4xx errors (like 404) are treated as "no data available"
      const status = error.response?.status;
      const isServerError = status >= 500;
      const isNetworkError = error.code === 'ERR_NETWORK' || error.message?.includes('Network');
      const isActualError = isServerError || isNetworkError;
      
      if (isActualError) {
        console.error('Error fetching programs:', error);
        addMessage('bot', `It looks like I'm having a moment fetching the programs right now. No worries though! 😊\n\nLet me help you in other ways - would you like to try again, explore other services, or connect with our support team who can assist you right away?`, [
          { text: 'Try Again', action: 'retry-fetch-programs' },
          { text: 'Explore Other Services', action: 'show-services' },
          { text: 'Get Help from Support', action: 'whatsapp' }
        ]);
      } else {
        // For 4xx errors or any other case, assume no programs available (not an error)
        addMessage('bot', `I checked, but there are currently no programs available for ${serviceName}. 😔\n\nWould you like me to notify you when programs become available for this service?`, [
          { text: 'Yes, Notify Me', action: `notify-service:${serviceName}` },
          { text: 'Check Another Service', action: 'show-services' }
        ]);
      }
    }
  };

  const fetchAllPrograms = async () => {
    try {
      setIsLoadingPrograms(true);
      setIsTyping(true);
      
      const response = await HttpInterceptor.get('/api/students/programs');
      
      if (response.status === 200) {
        const servicesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        
        setIsTyping(false);
        setIsLoadingPrograms(false);
        
        if (!servicesData || servicesData.length === 0) {
          addMessage('bot', `I checked, but there are currently no programs available across all services. 😔\n\nWould you like to contact our support team for more information, or check back later?`, [
            { text: 'Contact Support', action: 'whatsapp' },
            { text: 'Check Services', action: 'show-services' }
          ]);
          return;
        }
        
        // Collect all programs from all services
        const allPrograms = [];
        const serviceCounts = {};
        
        servicesData.forEach(service => {
          if (service.trainers && service.trainers.length > 0) {
            service.trainers.forEach(trainer => {
              if (trainer.programs && trainer.programs.length > 0) {
                trainer.programs.forEach(program => {
                  allPrograms.push({
                    ...program,
                    trainerName: trainer.name || 'Trainer',
                    trainerId: trainer.id || trainer._id,
                    serviceName: trainer.service || service.name || 'Service'
                  });
                  
                  // Count programs per service
                  const serviceName = trainer.service || service.name || 'Service';
                  serviceCounts[serviceName] = (serviceCounts[serviceName] || 0) + 1;
                });
              }
            });
          }
        });
        
        if (allPrograms.length === 0) {
          addMessage('bot', `I found services available, but there are currently no programs listed. 😔\n\nWould you like to check specific services or contact our support team?`, [
            { text: 'Check Services', action: 'show-services' },
            { text: 'Contact Support', action: 'whatsapp' }
          ]);
          return;
        }
        
        // Format programs for display
        let programsText = `Great! I found ${allPrograms.length} program${allPrograms.length > 1 ? 's' : ''} available across all services:\n\n`;
        
        // Group by service
        const programsByService = {};
        allPrograms.forEach(program => {
          const serviceName = program.serviceName;
          if (!programsByService[serviceName]) {
            programsByService[serviceName] = [];
          }
          programsByService[serviceName].push(program);
        });
        
        // Display programs grouped by service
        Object.entries(programsByService).slice(0, 5).forEach(([serviceName, programs]) => {
          programsText += `**${serviceName}** (${programs.length} program${programs.length > 1 ? 's' : ''})\n`;
          programs.slice(0, 3).forEach((program, index) => {
            programsText += `  ${index + 1}. ${program.name || 'Program'}\n`;
            if (program.trainerName) {
              programsText += `     👤 ${program.trainerName}\n`;
            }
          });
          if (programs.length > 3) {
            programsText += `     ...and ${programs.length - 3} more\n`;
          }
          programsText += `\n`;
        });
        
        if (Object.keys(programsByService).length > 5) {
          programsText += `\n...and ${Object.keys(programsByService).length - 5} more service${Object.keys(programsByService).length - 5 > 1 ? 's' : ''}!\n\n`;
        }
        
        programsText += `Would you like to see programs for a specific service, or view all programs to enroll?`;
        
        addMessage('bot', programsText, [
          { text: 'Select a Service', action: 'show-services' },
          { text: 'View All Programs', link: '/market' }
        ]);
      }
    } catch (error) {
      setIsTyping(false);
      setIsLoadingPrograms(false);
      console.error('Error fetching all programs:', error);
      addMessage('bot', `Hmm, I'm having a bit of trouble loading all programs at the moment. But don't worry - I'm here to help! 😊\n\nWould you like to try again, check a specific service, or connect with our support team who can show you everything available?`, [
        { text: 'Try Again', action: 'browse-all-programs' },
        { text: 'Check a Specific Service', action: 'show-services' },
        { text: 'Get Help from Support', action: 'whatsapp' }
      ]);
    }
  };

  const handleNotifyService = (serviceName) => {
    // Open the notification modal using the existing notification system
    setNotificationService(serviceName);
    setShowNotificationModal(true);
  };

  const handleQuickAction = (action) => {
    if (action === 'show-faqs') {
      showQuickQuestions();
      return;
    }
    
    if (action === 'show-services') {
      addMessage('bot', "Please select a service type to see available programs:", [
        { text: 'Yoga Trainer', action: 'select-service:Yoga Trainer' },
        { text: 'Dietitian', action: 'select-service:Dietitian' },
        { text: 'Gym Instructor', action: 'select-service:Gym Instructor' },
        { text: 'Zumba Instructor', action: 'select-service:Zumba Instructor' },
        { text: 'Reiki Healer', action: 'select-service:Reiki Healer' },
        { text: 'Astrologer', action: 'select-service:Astrologer' },
        { text: 'Numerologer', action: 'select-service:Numerologer' },
        { text: 'Ayurveda Practitioner', action: 'select-service:Ayurveda Practitioner' }
      ]);
      return;
    }
    
    if (typeof action === 'string' && action.startsWith('select-service:')) {
      const serviceName = action.replace('select-service:', '');
      addMessage('user', `I want to see programs for ${serviceName}`);
      fetchProgramsForService(serviceName);
      return;
    }
    
    if (typeof action === 'string' && action.startsWith('notify-service:')) {
      const serviceName = action.replace('notify-service:', '');
      handleNotifyService(serviceName);
      return;
    }
    
    if (action === 'browse-all-programs') {
      addMessage('user', 'I want to see all available programs');
      fetchAllPrograms();
      return;
    }
    
    if (action === 'retry-fetch-programs') {
      // Get the last user message about programs to retry
      const lastUserMessage = messages.filter(m => m.sender === 'user').pop();
      if (lastUserMessage && lastUserMessage.text.includes('program')) {
        // Try to extract service name or fetch all
        const serviceMatch = lastUserMessage.text.match(/for (.+)/i);
        if (serviceMatch) {
          fetchProgramsForService(serviceMatch[1]);
        } else {
          fetchAllPrograms();
        }
      } else {
        fetchAllPrograms();
      }
      return;
    }
    
    if (action === 'whatsapp') {
      const whatsappNumber = '918923162316';
      const whatsappMessage = encodeURIComponent("Hi! I need more help with Yogvital.");
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
      
      // Show typing indicator briefly
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('bot', "Perfect! Let me connect you with our support team on WhatsApp. You can park your message here, and our team will get back to you soon within official hours. They'll be happy to help! 💚", [
          { text: 'Open WhatsApp', link: whatsappUrl, external: true }
        ]);
        
        // Open WhatsApp after a brief delay
        setTimeout(() => {
          window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        }, 500);
      }, 800);
      return;
    }
    
    if (action === 'contact') {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addMessage('bot', "Great! You can reach our support team through our contact form, or I can connect you directly on WhatsApp!", [
          { text: 'Chat on WhatsApp', action: 'whatsapp' },
          { text: 'Go to Contact Form', link: '/contact-form' }
        ]);
      }, 600);
      return;
    }
    
    if (action === 'Contact Support') {
      handleQuickAction('whatsapp');
      return;
    }
    
    if (action === 'No, thanks!') {
      addMessage('bot', "You're welcome! Feel free to come back anytime if you need help. Have a wonderful day! 🌿✨");
      return;
    }
    
    // If it's a question, treat it as a message
    if (typeof action === 'string' && !action.includes('Go to') && !action.includes('View') && !action.includes('Browse') && !action.includes('Chat on') && !action.includes('Chat with')) {
      handleSendMessage(action);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Don't reset messages - keep them from sessionStorage
      // Only add greeting if no messages exist
      if (messages.length === 0) {
        addMessage('bot', greeting);
        lastInteractionRef.current = Date.now();
        startInactivityTimer();
      } else {
        // Restart inactivity timer if messages exist
        resetInactivityTimer();
      }
    } else {
      // Clear timer when closing
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    }
  };

  const refreshChat = () => {
    setMessages([]);
    try {
      sessionStorage.removeItem('chatbot-messages');
    } catch {
      // Ignore storage errors
    }
    // Add greeting
    addMessage('bot', greeting);
    lastInteractionRef.current = Date.now();
    startInactivityTimer();
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);

  // Only render for non-logged-in users (public/landing page)
  // Logged-in users have their own chatbots (StudentChatBot or TrainerChatBot)
  if (session) {
    return null;
  }

  return (
    <>
      {/* Chat Button - Positioned above WhatsApp button */}
      <div className={`fixed bottom-10 right-4 z-50 ${isOpen ? 'hidden' : 'block'}`}>
        <div className="relative group">
          <button
            onClick={toggleChat}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-full p-4 shadow-2xl ring-4 ring-white/30 hover:ring-white/50 hover:shadow-teal-500/50 transition-all duration-300 hover:scale-110"
            aria-label="Open chat assistant"
          >
            <FaComments className="text-2xl" />
            <span className="absolute -top-1 -right-1 bg-green-500 rounded-full w-4 h-4 flex items-center justify-center animate-pulse ring-2 ring-white">
            </span>
          </button>
          {/* Tooltip to distinguish from WhatsApp */}
          <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900/90 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap shadow-lg pointer-events-none">
            Ask me anything
            <div className="absolute left-full top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-900/90 rotate-45"></div>
          </div>
        </div>
      </div>

      {/* Chat Window - Positioned above WhatsApp button */}
      {isOpen && (
        <div
          ref={chatContainerRef}
          className="fixed bottom-10 right-4 z-50 w-96 h-[600px] bg-white rounded-2xl shadow-2xl ring-4 ring-white/30 flex flex-col overflow-hidden border-2 border-green-100"
          style={{ maxHeight: 'calc(100vh - 200px)' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {avatarError ? (
                <div className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center">
                  <span className="text-white font-semibold text-lg">P</span>
                </div>
              ) : (
                <img 
                  src={priyaAvatar} 
                  alt="Priya" 
                  className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
                  onError={() => setAvatarError(true)}
                />
              )}
              <div>
                <h3 className="font-semibold">Priya</h3>
                <p className="text-xs text-green-100">Online • Ready to help</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={refreshChat}
                className="text-white hover:text-green-200 transition-colors"
                aria-label="Refresh chat"
                title="Refresh chat"
              >
                <FaSync className="text-lg" />
              </button>
              <button
                onClick={toggleChat}
                className="text-white hover:text-green-200 transition-colors"
                aria-label="Close chat"
                title="Close chat"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex items-start gap-2 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.sender === 'bot' && (
                  avatarError ? (
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 font-semibold text-sm">P</span>
                    </div>
                  ) : (
                    <img 
                      src={priyaAvatar} 
                      alt="Priya" 
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      onError={() => setAvatarError(true)}
                    />
                  )
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.sender === 'user'
                      ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white'
                      : 'bg-white text-gray-800 shadow-md border border-gray-100'
                  }`}
                >
                  {msg.sender === 'bot' && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-green-600">Priya</span>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.text}</p>
                  
                  {/* Quick Actions */}
                  {msg.quickActions && (
                    <div className="mt-3 space-y-2">
                      {msg.quickActions.map((action, actionIndex) => {
                        // Handle object actions with link property
                        if (typeof action === 'object' && action.link) {
                          // External link (WhatsApp)
                          if (action.external) {
                            return (
                              <a
                                key={actionIndex}
                                href={action.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full bg-green-50 hover:bg-green-100 text-green-700 text-xs font-medium px-3 py-2 rounded-lg transition-colors text-center border border-green-200 flex items-center justify-center gap-2"
                                onClick={() => {
                                  // Keep chat open for external links too
                                }}
                              >
                                <FaWhatsapp className="text-green-600" />
                                {action.text}
                              </a>
                            );
                          }
                          // Internal link - don't close chat, just navigate
                          return (
                            <Link
                              key={actionIndex}
                              to={action.link}
                              className="block w-full bg-green-50 hover:bg-green-100 text-green-700 text-xs font-medium px-3 py-2 rounded-lg transition-colors text-center border border-green-200"
                              onClick={() => {
                                // Keep chat open - don't close it
                                // User can come back and see the conversation
                                // Scroll to hash if present
                                if (action.link.includes('#')) {
                                  setTimeout(() => {
                                    const hash = action.link.split('#')[1];
                                    const element = document.getElementById(hash);
                                    if (element) {
                                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                  }, 100);
                                }
                              }}
                            >
                              {action.text}
                            </Link>
                          );
                        }
                        // Handle object actions with action property (like select-service)
                        if (typeof action === 'object' && action.action) {
                          return (
                            <button
                              key={actionIndex}
                              onClick={() => handleQuickAction(action.action)}
                              className="block w-full bg-green-50 hover:bg-green-100 text-green-700 text-xs font-medium px-3 py-2 rounded-lg transition-colors text-left border border-green-200"
                            >
                              {action.text}
                            </button>
                          );
                        }
                        // Handle string actions
                        return (
                          <button
                            key={actionIndex}
                            onClick={() => handleQuickAction(action)}
                            className="block w-full bg-green-50 hover:bg-green-100 text-green-700 text-xs font-medium px-3 py-2 rounded-lg transition-colors text-left border border-green-200"
                          >
                            {action}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {(isTyping || isLoadingPrograms) && (
              <div className="flex items-start gap-2 justify-start">
                {avatarError ? (
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-600 font-semibold text-sm">P</span>
                  </div>
                ) : (
                  <img 
                    src={priyaAvatar} 
                    alt="Priya" 
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                    onError={() => setAvatarError(true)}
                  />
                )}
                <div className="bg-white text-gray-800 rounded-2xl px-4 py-3 shadow-md border border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-green-600">Priya</span>
                    <span className="text-xs text-gray-500 italic">is typing...</span>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                disabled={isTyping}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isTyping}
                className="bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg px-4 py-2 hover:from-green-700 hover:to-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <FaPaperPlane />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              💚 We're here to help!
            </p>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <TrainerAvailabilityNotificationModal
        isOpen={showNotificationModal}
        onClose={() => {
          setShowNotificationModal(false);
          setNotificationService(null);
        }}
        trainerId={null}
        trainerName={null}
        programId={null}
        programName={null}
        service={notificationService}
      />
    </>
  );
};

export default ChatBot;

